import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useUnsavedChanges } from "@vd/ui";
import { ChevronRight } from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import { usersQuery } from "../../../api/admin";
import {
  type AgentSettings,
  assistantSettingsQuery,
  assistantUsageQuery,
  cachedShare,
  campaignsApi,
  campaignTreeQuery,
  ck,
  ENGINE_DEFAULTS,
  fundamentalsQuery,
  type MonthSpend,
  monthLabel,
  usd,
} from "../../../api/campaigns";
import { errorMessage } from "../../../api/client";
import { TASK_LABEL } from "../../../api/research";
import { requireAdmin } from "../../../auth/useMe";
import { EngineSettingsGroup } from "../../../components/assistant/EngineSettingsGroup";
import { LimitField } from "../../../components/LimitField";
import { LeadSources } from "../../../components/research/LeadSources";
import { ReviewSettingsGroup } from "../../../components/reviews/ReviewSettingsGroup";
import { toast } from "../../../components/toast";
import {
  Badge,
  Button,
  Card,
  cx,
  ErrorState,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  Stat,
  Switch,
  Tabs,
  Textarea,
} from "../../../components/ui";
import { draftErrors, draftsOf, draftValues, limitError } from "../../../lib/limits";
import { useNavigate } from "../../../lib/navigate";

const TABS = ["fundamentals", "settings", "sources", "spend"] as const;
type Tab = (typeof TABS)[number];

export const Route = createFileRoute("/admin/ai/")({
  staticData: { crumb: { label: "Assistant", parents: [] } },
  validateSearch: (s) => z.object({ tab: z.enum(TABS).optional().catch(undefined) }).parse(s),
  beforeLoad: requireAdmin,
  component: AssistantPage,
});

function AssistantPage() {
  const { tab = "fundamentals" } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  return (
    <>
      <PageHeader title="Assistant" />
      <Tabs<Tab>
        label="Assistant"
        value={tab}
        onChange={(t) => navigate({ search: { tab: t === "fundamentals" ? undefined : t }, replace: true })}
        tabs={[
          { value: "fundamentals", label: "Company fundamentals" },
          { value: "settings", label: "Settings" },
          { value: "sources", label: "Lead sources" },
          { value: "spend", label: "Spend" },
        ]}
      />
      {tab === "fundamentals" ? (
        <Fundamentals />
      ) : tab === "settings" ? (
        <Settings />
      ) : tab === "sources" ? (
        <LeadSources />
      ) : (
        <Spend />
      )}
    </>
  );
}

// ---------- fundamentals ----------

function Fundamentals() {
  const qc = useQueryClient();
  const q = useQuery(fundamentalsQuery);
  const [text, setText] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState(false);
  // Going back asks first while the text holds unsaved changes.
  useUnsavedChanges(text !== null && q.data !== undefined && text !== q.data.body_md);
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const value = text ?? q.data.body_md;
  const dirty = text !== null && text !== q.data.body_md;
  const save = async () => {
    setBusy(true);
    try {
      const next = await campaignsApi.saveFundamentals({ body_md: value, version: q.data.version });
      qc.setQueryData(ck.fundamentals, next);
      qc.invalidateQueries({ queryKey: ck.versions });
      setText(null);
      toast("Saved. Every rep's assistant uses it from the next message.");
    } catch (e) {
      toast(errorMessage(e), "danger");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
      <Card
        title="What every assistant knows about the company"
        actions={
          <Button size="sm" variant="primary" disabled={!dirty} busy={busy} onClick={save}>
            Save
          </Button>
        }
      >
        <div className="flex flex-col gap-1 p-4">
          <Textarea
            aria-label="Company fundamentals"
            rows={18}
            maxLength={20000}
            value={value}
            placeholder="Products and prices, who you sell to, how you sound on the phone, what never to promise."
            onChange={(e) => setText(e.target.value)}
          />
          <div className="flex justify-between text-caption text-ink-4">
            <span>
              {q.data.version
                ? `Version ${q.data.version}${q.data.updated_by_name ? `, ${q.data.updated_by_name}` : ""}`
                : "Not written yet"}
            </span>
            <span className="tnum">{value.length} / 20,000</span>
          </div>
          <button
            type="button"
            className="flex w-fit items-center gap-1 pt-2 text-caption text-ink-3 hover:text-ink-2"
            aria-expanded={history}
            onClick={() => setHistory((h) => !h)}
          >
            <ChevronRight className={cx("size-3.5 transition-transform", history && "rotate-90")} />
            Earlier versions
          </button>
          {history ? <Versions onRestore={(t) => setText(t)} /> : null}
        </div>
      </Card>
      <Preview />
    </div>
  );
}

function Versions({ onRestore }: { onRestore: (text: string) => void }) {
  const q = useQuery({ queryKey: ck.versions, queryFn: campaignsApi.versions });
  if (!q.data) return null;
  return (
    <ul className="flex flex-col">
      {q.data.items.map((v) => (
        <li key={v.version} className="flex h-9 items-center justify-between gap-3 border-hairline border-b">
          <span className="text-caption text-ink-2">
            Version {v.version} · {v.updated_by_name ?? "Someone"} ·{" "}
            {new Date(v.updated_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
          </span>
          <Button size="sm" variant="ghost" onClick={() => onRestore(v.body_md)}>
            Use this one
          </Button>
        </li>
      ))}
    </ul>
  );
}

function Preview() {
  const users = useQuery(usersQuery);
  const tree = useQuery(campaignTreeQuery);
  const [userId, setUserId] = useState("");
  const [campaignId, setCampaignId] = useState("");
  const preview = useQuery({
    queryKey: ["admin", "assistant", "preview", userId, campaignId],
    queryFn: () => campaignsApi.preview(userId, campaignId || null),
    enabled: Boolean(userId),
  });
  const flat = (tree.data?.items ?? []).flatMap(function walk(n): { value: string; label: string }[] {
    return [
      { value: n.id, label: n.name },
      ...n.children.flatMap(walk).map((x) => ({ ...x, label: `${n.name} / ${x.label}` })),
    ];
  });
  return (
    <Card title="What a rep's assistant reads">
      <div className="flex flex-col gap-3 p-4">
        <Select
          aria-label="Rep"
          placeholder="Pick a rep"
          value={userId}
          onValueChange={setUserId}
          options={(users.data ?? [])
            .filter((u) => u.status === "active")
            .map((u) => ({ value: u.id, label: u.name }))}
        />
        <Select
          aria-label="Campaign"
          value={campaignId}
          onValueChange={setCampaignId}
          options={[{ value: "", label: "No campaign" }, ...flat]}
        />
        {preview.isError ? (
          <p role="alert" className="text-caption text-danger-ink">
            {errorMessage(preview.error)}
          </p>
        ) : null}
        {preview.data ? (
          <>
            <ul className="flex flex-col gap-1">
              {preview.data.sections.map((s) => (
                <li key={s.name} className="flex justify-between text-caption">
                  <span className="text-ink-2">{s.name}</span>
                  <span className="text-ink-3 tnum">
                    {s.trimmed ? (
                      <Badge tone="warn">Left out</Badge>
                    ) : (
                      `${s.chars.toLocaleString()} characters`
                    )}
                  </span>
                </li>
              ))}
            </ul>
            <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-md bg-well p-3 font-mono text-[12px] text-ink-2">
              {preview.data.prompt}
            </pre>
          </>
        ) : null}
      </div>
    </Card>
  );
}

// ---------- settings ----------

function Settings() {
  const q = useQuery(assistantSettingsQuery);
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  return <SettingsForm key={q.data.updated_at ?? "new"} initial={q.data.settings} />;
}

/** Settings from a server that predates reviews get the review defaults, so a save sends them. */
function withReviewDefaults(s: AgentSettings): AgentSettings {
  return {
    ...s,
    perf_review_enabled: s.perf_review_enabled ?? true,
    perf_review_reps_can_run: s.perf_review_reps_can_run ?? false,
    perf_review_schedule: s.perf_review_schedule ?? { daily: false, weekly: false, monthly: false },
    perf_review_max_usd_per_run: s.perf_review_max_usd_per_run ?? 0.5,
    perf_review_runs_per_rep_per_day: s.perf_review_runs_per_rep_per_day ?? 3,
    perf_review_scheduled_max_usd_month: s.perf_review_scheduled_max_usd_month ?? 20,
    perf_review_audio: s.perf_review_audio ?? true,
    turn_max_usd: s.turn_max_usd ?? 1,
  };
}

function SettingsForm({ initial }: { initial: AgentSettings }) {
  const qc = useQueryClient();
  const [s, setS] = useState(() => withReviewDefaults(initial));
  const [budget, setBudget] = useState(() =>
    draftsOf({
      daily_usd_per_user: initial.daily_usd_per_user,
      monthly_usd_tenant: initial.monthly_usd_tenant,
      turn_max_usd: initial.turn_max_usd ?? 1,
      perf_review_max_usd_per_run: initial.perf_review_max_usd_per_run ?? 0.5,
      perf_review_runs_per_rep_per_day: initial.perf_review_runs_per_rep_per_day ?? 3,
      perf_review_scheduled_max_usd_month: initial.perf_review_scheduled_max_usd_month ?? 20,
    }),
  );
  // The engine's ceiling is typed separately: it lives under `engine`, not beside the budgets.
  const [engineTurn, setEngineTurn] = useState(() =>
    String(initial.engine?.turn_max_usd ?? ENGINE_DEFAULTS.turn_max_usd),
  );
  const [busy, setBusy] = useState(false);
  const [more, setMore] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const invalid =
    Object.keys(draftErrors(budget)).length > 0 ||
    (s.engine !== undefined && limitError("engine_turn_max_usd", engineTurn) !== null);
  const next = invalid
    ? s
    : {
        ...s,
        ...draftValues(budget),
        // An empty model field means the default model.
        ...(s.engine
          ? {
              engine: {
                ...s.engine,
                chat_model: s.engine.chat_model.trim() || ENGINE_DEFAULTS.chat_model,
                work_model: s.engine.work_model.trim() || ENGINE_DEFAULTS.work_model,
                turn_max_usd: Number(engineTurn.trim()),
              },
            }
          : {}),
      };
  const dirty = JSON.stringify(next) !== JSON.stringify(withReviewDefaults(initial));
  useUnsavedChanges(dirty);
  const save = async () => {
    setBusy(true);
    setSaveError(null);
    try {
      const saved = await campaignsApi.saveSettings(next);
      qc.setQueryData(ck.settings, saved);
      toast("Assistant settings saved.");
    } catch (e) {
      // A 422 carries the server's reason (a limit out of range, say); keep it next to the form.
      setSaveError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card
      className="max-w-[720px]"
      title="Assistant"
      actions={
        <Button size="sm" variant="primary" disabled={!dirty || invalid} busy={busy} onClick={save}>
          Save
        </Button>
      }
    >
      <div className="flex flex-col gap-4 p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-col">
            <span className="text-ui">Assistant on</span>
            <span className="text-caption text-ink-3">
              Off closes it for every rep. Report a problem stays.
            </span>
          </div>
          <Switch checked={s.enabled} label="Assistant on" onChange={(v) => setS({ ...s, enabled: v })} />
        </div>
        <div className="grid grid-cols-2 gap-4 max-[640px]:grid-cols-1">
          <LimitField
            name="daily_usd_per_user"
            value={budget.daily_usd_per_user}
            onChange={(v) => setBudget({ ...budget, daily_usd_per_user: v })}
          />
          <LimitField
            name="monthly_usd_tenant"
            value={budget.monthly_usd_tenant}
            onChange={(v) => setBudget({ ...budget, monthly_usd_tenant: v })}
          />
          <LimitField
            name="turn_max_usd"
            value={budget.turn_max_usd}
            onChange={(v) => setBudget({ ...budget, turn_max_usd: v })}
          />
        </div>
        {s.engine ? (
          <EngineSettingsGroup
            engine={s.engine}
            onChange={(engine) => setS({ ...s, engine })}
            turnDraft={engineTurn}
            onTurnDraft={setEngineTurn}
          />
        ) : null}
        <ReviewSettingsGroup
          settings={s}
          onChange={setS}
          drafts={budget}
          onDrafts={(d) => setBudget({ ...budget, ...d })}
        />
        {saveError ? (
          <p role="alert" className="text-caption text-danger-ink">
            {saveError}
          </p>
        ) : null}
        <button
          type="button"
          className="flex w-fit items-center gap-1 text-caption text-ink-3 hover:text-ink-2"
          aria-expanded={more}
          onClick={() => setMore((m) => !m)}
        >
          <ChevronRight className={cx("size-3.5 transition-transform", more && "rotate-90")} />
          Models and Odoo tags
        </button>
        {more ? (
          <div className="grid grid-cols-2 gap-4 max-[640px]:grid-cols-1">
            <Field label="Chat model">
              <Input
                value={s.models.chat}
                onChange={(e) => setS({ ...s, models: { ...s.models, chat: e.target.value } })}
              />
            </Field>
            <Field label="Planning model">
              <Input
                value={s.models.plan}
                onChange={(e) => setS({ ...s, models: { ...s.models, plan: e.target.value } })}
              />
            </Field>
            <Field label="Campaign tags go under">
              <Input
                value={s.tag_namespaces.campaign}
                onChange={(e) =>
                  setS({ ...s, tag_namespaces: { ...s.tag_namespaces, campaign: e.target.value } })
                }
              />
            </Field>
            <Field label="Import tags go under">
              <Input
                value={s.tag_namespaces.imports}
                onChange={(e) =>
                  setS({ ...s, tag_namespaces: { ...s.tag_namespaces, imports: e.target.value } })
                }
              />
            </Field>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

// ---------- spend ----------

function Spend() {
  const q = useQuery(assistantUsageQuery);
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const u = q.data;
  const pct = u.monthly_cap_usd > 0 ? Math.round((u.month_usd / u.monthly_cap_usd) * 100) : 0;
  const research = u.by_task.find((t) => t.task === "research")?.usd ?? 0;
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="This month" value={usd(u.month_usd)} sub={`${pct}% of ${usd(u.monthly_cap_usd)}`} />
        <Stat label="Lead generation" value={usd(research)} sub="This month" />
        <Stat label="Per rep, per day" value={usd(u.daily_cap_usd)} sub="Limit" />
        <Stat label="Today, all reps" value={usd(u.reps.reduce((n, r) => n + r.today_usd, 0))} />
      </div>
      {u.by_task.length ? (
        <Card title="By task">
          <table className="w-full text-body">
            <tbody>
              {u.by_task.map((t) => (
                <tr key={t.task} className="h-10 border-hairline border-t first:border-t-0">
                  <td className="px-4">{TASK_LABEL[t.task] ?? t.task}</td>
                  <td className="px-4 text-right tnum">{usd(t.usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : null}
      <Months months={u.months ?? []} reps={u.rep_months ?? []} />
      <Card title="By rep">
        <table className="w-full text-body">
          <thead>
            <tr className="h-8 text-left text-ui text-ink-3">
              <th className="px-4 font-medium">Rep</th>
              <th className="px-4 text-right font-medium">Today</th>
              <th className="px-4 text-right font-medium">This month</th>
              <th className="px-4 text-right font-medium">Model calls today</th>
            </tr>
          </thead>
          <tbody>
            {u.reps.map((r) => (
              <tr key={r.user_id} className="h-10 border-hairline border-t">
                <td className="px-4">{r.name}</td>
                <td
                  className={cx(
                    "px-4 text-right tnum",
                    r.today_usd >= u.daily_cap_usd && u.daily_cap_usd > 0 && "text-warning",
                  )}
                >
                  {usd(r.today_usd)}
                </td>
                <td className="px-4 text-right tnum">{usd(r.month_usd)}</td>
                <td className="px-4 text-right text-ink-2 tnum">{r.model_calls_today}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

/** Spend per month: the company's last six months, and each rep's in the month picked. */
function Months({
  months,
  reps,
}: {
  months: MonthSpend[];
  reps: (MonthSpend & { user_id: string; name: string })[];
}) {
  const [picked, setPicked] = useState<string>("");
  if (!months.length) return null;
  const month = picked || months[0]?.month || "";
  const mine = reps.filter((r) => r.month === month);
  const head = (
    <tr className="h-8 text-left text-ui text-ink-3">
      <th className="px-4 font-medium">{" "}</th>
      <th className="px-4 text-right font-medium">Spend</th>
      <th className="px-4 text-right font-medium">Turns</th>
      <th className="px-4 text-right font-medium">Model calls</th>
      <th className="px-4 text-right font-medium">Lookups</th>
      <th className="px-4 text-right font-medium">Cached input</th>
    </tr>
  );
  const row = (key: string, label: string, m: MonthSpend) => (
    <tr key={key} className="h-10 border-hairline border-t">
      <td className="px-4">{label}</td>
      <td className="px-4 text-right tnum">{usd(m.usd)}</td>
      <td className="px-4 text-right text-ink-2 tnum">{m.turns.toLocaleString()}</td>
      <td className="px-4 text-right text-ink-2 tnum">{m.model_calls.toLocaleString()}</td>
      <td className="px-4 text-right text-ink-2 tnum">{m.tool_calls.toLocaleString()}</td>
      <td className="px-4 text-right text-ink-2 tnum">{cachedShare(m)}</td>
    </tr>
  );
  return (
    <>
      <Card title="Company by month">
        <table className="w-full text-body">
          <thead>{head}</thead>
          <tbody>{months.map((m) => row(m.month, monthLabel(m.month), m))}</tbody>
        </table>
      </Card>
      <Card
        title="Reps by month"
        actions={
          <Select
            aria-label="Month"
            value={month}
            onValueChange={setPicked}
            options={months.map((m) => ({ value: m.month, label: monthLabel(m.month) }))}
          />
        }
      >
        {mine.length ? (
          <table className="w-full text-body">
            <thead>{head}</thead>
            <tbody>{mine.map((r) => row(r.user_id, r.name, r))}</tbody>
          </table>
        ) : (
          <p className="px-4 py-3 text-body text-ink-3">No Assistant spend in {monthLabel(month)}.</p>
        )}
      </Card>
    </>
  );
}
